import React, { useState } from 'react';

export default function AuthenticatorSetup({ qrCodeUrl, secret, loading, onComplete, onBack }) {
  const [deviceName, setDeviceName] = useState('My Authenticator');
  const [verificationCode, setVerificationCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = () => {
    if (!deviceName?.trim()) {
      setError('Please enter a device name');
      return;
    }
    if (!verificationCode?.trim() || verificationCode?.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setError('');
    onComplete(deviceName, verificationCode);
  };

  const copyToClipboard = () => {
    navigator.clipboard?.writeText(secret);
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="mb-4 text-indigo-600 hover:text-indigo-800 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to method selection
      </button>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Up Authenticator App</h2>
      <p className="text-gray-600 mb-8">Scan the QR code with your authenticator app or enter the secret key manually</p>
      <div className="space-y-6">
        {/* QR Code Section */}
        <div className="flex flex-col items-center">
          {qrCodeUrl ? (
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <img src={qrCodeUrl} alt="QR Code for authenticator app setup" className="w-64 h-64" />
            </div>
          ) : (
            <div className="w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          )}

          <button
            onClick={() => setShowManualEntry(!showManualEntry)}
            className="mt-4 text-sm text-indigo-600 hover:text-indigo-800"
          >
            {showManualEntry ? 'Hide' : 'Show'} manual entry key
          </button>
        </div>

        {/* Manual Entry Section */}
        {showManualEntry && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Manual Entry Key:</p>
            <div className="flex items-center">
              <code className="flex-1 bg-white px-4 py-2 rounded border border-gray-300 font-mono text-sm">
                {secret}
              </code>
              <button
                onClick={copyToClipboard}
                className="ml-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Setup Instructions:</h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
            <li>Download an authenticator app (Google Authenticator, Authy, Microsoft Authenticator)</li>
            <li>Open the app and select "Add Account" or scan QR code option</li>
            <li>Scan the QR code above or manually enter the secret key</li>
            <li>Enter the 6-digit code from your app below to verify</li>
          </ol>
        </div>

        {/* Device Name Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Device Name
          </label>
          <input
            type="text"
            value={deviceName}
            onChange={(e) => setDeviceName(e?.target?.value)}
            placeholder="e.g., My iPhone Authenticator"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Verification Code Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Verification Code
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
            placeholder="Enter 6-digit code"
            maxLength={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center text-2xl font-mono"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleVerify}
            disabled={loading}
            className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify and Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}